import type { BreakerCharacteristic, PanelRow, RowLoad, RowLoadType } from "./panels.ts";

/**
 * Auto-säkring: givet en lista med `RowLoad` (ljuspunkter, uttag, motorer) väljs
 * närmaste standardsäkring med rimlig marginal.
 *
 * Metod:
 *   1. Summera effekt (W) från alla laster: `wattage × quantity`.
 *      Saknas värde används defaulter per lasttyp (ljuspunkt 18 W, uttag 2300 W).
 *   2. Effektfaktor (cosφ) räknas som last-viktat medel; motor/EV → 0,85,
 *      övriga → 1,0.
 *   3. Ampere = W / (U × cosφ) för 1-fas, W / (U × √3 × cosφ) för 3-fas.
 *   4. Välj närmaste standard-storlek (6/10/13/16/20/25/32/40/50/63/80/100 A)
 *      ≥ ampere × 1,25 (25 % marginal).
 *   5. Karakteristik: C om någon last är motor/EV (hög startström), annars B.
 *
 * Vi väljer *inte* JFB/JFA automatiskt — det är en separat säkerhetsregel som
 * beror på placering (våtutrymme, ute) och lagkrav. Användaren får förslag via
 * `panelRules.ts`-varningarna.
 */

/** Standard-MCB-storlekar enligt IEC 60898. */
export const STANDARD_MCB_AMPS = [
  6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125, 160,
] as const;

/** Förvald effekt per last-typ (W) — används när användaren inte anger värde. */
export const DEFAULT_WATTAGE: Record<RowLoadType, number> = {
  lamp: 18,          // LED-spot/armatur
  socket: 2300,      // 1-fas 10 A uttag i max
  motor: 1100,       // liten motor, ca 1,5 hk
  heater: 2000,      // element
  stove: 8000,       // 3-fas spis
  "ev-charger": 3680, // 1-fas 16 A
  "water-heater": 3000,
  other: 500,
};

/** Förvald cosφ per last-typ. */
export const DEFAULT_COS_PHI: Record<RowLoadType, number> = {
  lamp: 0.95,
  socket: 1.0,
  motor: 0.85,
  heater: 1.0,
  stove: 1.0,
  "ev-charger": 0.95,
  "water-heater": 1.0,
  other: 1.0,
};

/** Svenska etiketter för lasttyper. */
export const LOAD_TYPE_LABEL: Record<RowLoadType, string> = {
  lamp: "Ljuspunkt",
  socket: "Uttag",
  motor: "Motor",
  heater: "Värmare",
  stove: "Spis",
  "ev-charger": "EV-laddare",
  "water-heater": "Varmvattenberedare",
  other: "Annat",
};

/** Nettolast i watt och ampere räknat från en laster-lista. */
export type LoadTotals = {
  watts: number;
  amps: number;
  cosPhi: number;
};

/**
 * Räknar ut total watt och ampere för en laster-lista. Antaganden: alla laster
 * sitter på samma grupp (parallellt på samma säkring).
 *
 * @param loads — laster att summera
 * @param opts.poles — antal poler på säkringen (1 = 230 V, 3 = 400 V)
 * @param opts.voltage — manuell spänning, default 230 V / 400 V beroende på poler
 */
export function computeLoadTotals(
  loads: ReadonlyArray<RowLoad>,
  opts: { poles?: 1 | 3; voltage?: number } = {},
): LoadTotals {
  if (loads.length === 0) return { watts: 0, amps: 0, cosPhi: 1 };
  const poles = opts.poles ?? 1;
  const voltage = opts.voltage ?? (poles === 3 ? 400 : 230);

  let watts = 0;
  let weightedCosPhi = 0;
  for (const l of loads) {
    const w = (l.wattage ?? DEFAULT_WATTAGE[l.type]) * (l.quantity ?? 1);
    const cp = l.cosPhi ?? DEFAULT_COS_PHI[l.type];
    watts += w;
    weightedCosPhi += w * cp;
  }
  const cosPhi = watts > 0 ? weightedCosPhi / watts : 1;
  const denom = poles === 3 ? voltage * Math.sqrt(3) * cosPhi : voltage * cosPhi;
  const amps = denom > 0 ? watts / denom : 0;
  return { watts, amps, cosPhi };
}

/** Resultat från `suggestFuse`. */
export type SuggestedFuse = {
  ampRating: number;
  characteristic: BreakerCharacteristic;
  poles: 1 | 3;
  /** Katalog-nyckel (t.ex. "MCB-1P-B16") — matchar `DIN_CATALOG.key`. */
  component: string;
  /** Diagnostik: total last (W/A) som låg till grund för förslaget. */
  totals: LoadTotals;
};

/**
 * Föreslår säkring utifrån laster. Returnerar null om listan är tom.
 */
export function suggestFuse(
  loads: ReadonlyArray<RowLoad>,
  opts: { poles?: 1 | 3; marginPct?: number } = {},
): SuggestedFuse | null {
  if (loads.length === 0) return null;
  const poles = opts.poles ?? pickDefaultPoles(loads);
  const margin = 1 + (opts.marginPct ?? 25) / 100;

  const totals = computeLoadTotals(loads, { poles });
  const required = totals.amps * margin;

  const rating =
    STANDARD_MCB_AMPS.find((r) => r >= required) ??
    STANDARD_MCB_AMPS[STANDARD_MCB_AMPS.length - 1]!;

  const hasInrush = loads.some(
    (l) => l.type === "motor" || l.type === "ev-charger",
  );
  const characteristic: BreakerCharacteristic = hasInrush ? "C" : "B";
  const component = `MCB-${poles}P-${characteristic}${rating}`;

  return { ampRating: rating, characteristic, poles, component, totals };
}

/**
 * Heuristik för poler när användaren inte angett: 3-fas om spis eller total
 * effekt > 4,6 kVA (dvs. 20 A på 1-fas). Annars 1-fas.
 */
function pickDefaultPoles(loads: ReadonlyArray<RowLoad>): 1 | 3 {
  if (loads.some((l) => l.type === "stove")) return 3;
  const totals = computeLoadTotals(loads, { poles: 1 });
  return totals.amps > 20 ? 3 : 1;
}

/**
 * Bygger en patch (ampRating/poles/characteristic/component/loadWatts) från
 * en `SuggestedFuse`. Används av mutations-layret efter att laster ändrats.
 */
export function suggestedFusePatch(suggestion: SuggestedFuse): Partial<PanelRow> {
  return {
    ampRating: suggestion.ampRating,
    poles: suggestion.poles,
    characteristic: suggestion.characteristic,
    component: suggestion.component,
    loadWatts: Math.round(suggestion.totals.watts),
  };
}
